/**
 * React Hook for Adaptive Learning Integration
 * 
 * Provides comprehensive adaptive learning functionality including performance tracking,
 * difficulty adjustment, question selection, and personalized recommendations.
 * 
 * The hook automatically tracks question attempts, calculates accuracy, adjusts difficulty
 * based on performance, and provides analytics and insights for personalized learning paths.
 * 
 * @module useAdaptiveLearning
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  adaptiveLearningEngine, 
  saveAdaptiveLearningData, 
  loadAdaptiveLearningData,
  initializeAdaptiveLearning,
  DIFFICULTY_LEVELS 
} from '../utils/adaptiveLearning';
import { useUser } from '../contexts/UserContext';

/**
 * Hook for adaptive learning system integration.
 * 
 * Automatically initializes when a user is available. Tracks performance, adjusts difficulty,
 * and provides personalized learning recommendations based on user progress.
 * 
 * @returns {Object} Adaptive learning functions and state
 * @returns {Function} returns.trackQuestionAttempt - Track a single question attempt
 * @returns {Function} returns.startQuestion - Start timing a new question
 * @returns {Function} returns.startSession - Begin a new learning session for a subject
 * @returns {Function} returns.endSession - End a session and track session-level metrics
 * @returns {Function} returns.getCurrentDifficulty - Get recommended difficulty for a subject
 * @returns {Function} returns.getDifficultyAdjustment - Get difficulty adjustment with explanation
 * @returns {Function} returns.selectQuestions - Get smart question selection based on performance
 * @returns {Function} returns.getSubjectAnalytics - Get comprehensive analytics for a subject
 * @returns {Function} returns.getSubjectMastery - Get mastery level string ('beginner', 'developing', etc.)
 * @returns {Function} returns.getSubjectAccuracy - Get accuracy percentage for a subject
 * @returns {Function} returns.getSubjectStreak - Get current correct answer streak
 * @returns {Function} returns.getLearningRecommendations - Get personalized learning path recommendations
 * @returns {Function} returns.getProgressIndicators - Get UI-friendly progress indicators
 * @returns {Function} returns.getEncouragementMessage - Get personalized encouragement message
 * @returns {boolean} returns.isInitialized - Whether the adaptive learning system is ready
 * @returns {Object} returns.performanceData - Current performance data by subject
 * @returns {Array} returns.recommendations - Current learning path recommendations
 * 
 * @example
 * const {
 *   trackQuestionAttempt,
 *   getCurrentDifficulty,
 *   getEncouragementMessage
 * } = useAdaptiveLearning();
 * 
 * // Track an answer
 * const result = trackQuestionAttempt('math', mathQuestion, {
 *   isCorrect: true,
 *   responseTime: 2500
 * });
 * 
 * // Get personalized feedback
 * const message = getEncouragementMessage('math', { isCorrect: true });
 * 
 * // Get recommended difficulty
 * const difficulty = getCurrentDifficulty('math', 'medium');
 */
export const useAdaptiveLearning = () => {
  const { user } = useUser();
  const [isInitialized, setIsInitialized] = useState(false);
  const [performanceData, setPerformanceData] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const sessionStartTime = useRef(Date.now());
  const questionStartTime = useRef(Date.now());

  // Initialize the adaptive learning system
  useEffect(() => {
    if (user && !isInitialized) {
      initializeAdaptiveLearning();
      setIsInitialized(true);
    }
  }, [user, isInitialized]);

  /**
   * Tracks a question attempt and updates performance analytics.
   * 
   * @param {string} subject - Subject identifier (e.g., 'math', 'phonics')
   * @param {Object} questionData - Question object being answered
   * @param {Object} result - Result of the attempt
   * @param {boolean} result.isCorrect - Whether the answer was correct
   * @param {number} [result.responseTime] - Time taken to answer in milliseconds (auto-calculated if omitted)
   * @returns {Object|null} Performance entry object or null if not initialized
   * 
   * @example
   * trackQuestionAttempt('math', { id: 'q1', question: '2+2?' }, {
   *   isCorrect: true,
   *   responseTime: 2500
   * });
   */
  const trackQuestionAttempt = useCallback((subject, questionData, result) => {
    if (!user || !isInitialized) return;

    const performanceEntry = adaptiveLearningEngine.trackPerformance(
      user.id,
      subject,
      questionData,
      {
        ...result,
        responseTime: result.responseTime || (Date.now() - questionStartTime.current)
      }
    );

    // Update local performance data
    setPerformanceData(prev => ({
      ...prev,
      [subject]: adaptiveLearningEngine.getPerformanceAnalytics(user.id, subject)
    }));

    // Save data after tracking
    saveAdaptiveLearningData();

    return performanceEntry;
  }, [user, isInitialized]);

  /**
   * Starts timing a new question. Call before displaying the question.
   * Used to calculate response time for the next trackQuestionAttempt call.
   * 
   * @returns {void}
   * 
   * @example
   * startQuestion(); // Start timer
   * // ... show question to user ...
   * // ... user answers ...
   * trackQuestionAttempt(subject, question, { isCorrect: true }); // Response time auto-calculated
   */
  const startQuestion = useCallback(() => {
    questionStartTime.current = Date.now();
  }, []);

  /**
   * Gets the recommended difficulty level for a subject based on performance.
   * 
   * @param {string} subject - Subject identifier
   * @param {string} [currentDifficulty=DIFFICULTY_LEVELS.MEDIUM] - Current difficulty level
   * @returns {string} Recommended difficulty level ('easy', 'medium', 'hard')
   * 
   * @example
   * const difficulty = getCurrentDifficulty('math', 'medium');
   * // Returns: 'easy', 'medium', or 'hard' based on user performance
   */
  const getCurrentDifficulty = useCallback((subject, currentDifficulty = DIFFICULTY_LEVELS.MEDIUM) => {
    if (!user || !isInitialized) return currentDifficulty;
    
    return adaptiveLearningEngine.adjustDifficulty(user.id, subject, currentDifficulty);
  }, [user, isInitialized]);

  /**
   * Selects questions from a pool based on user performance and learning needs.
   * Falls back to random selection if adaptive learning is not initialized.
   * 
   * @param {string} subject - Subject identifier
   * @param {Array} questionPool - Available questions to choose from
   * @param {number} [count=10] - Number of questions to select
   * @returns {Array} Selected questions optimized for the user's learning needs
   * 
   * @example
   * const selected = selectQuestions('math', allMathQuestions, 10);
   * // Returns 10 questions selected based on user's weak areas
   */
  const selectQuestions = useCallback((subject, questionPool, count = 10) => {
    if (!user || !isInitialized) {
      // Fallback to random selection
      const shuffled = [...questionPool].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, count);
    }
    
    return adaptiveLearningEngine.selectQuestions(user.id, subject, questionPool, count);
  }, [user, isInitialized]);

  /**
   * Gets personalized learning path recommendations based on performance across subjects.
   * 
   * @param {Array<string>} availableSubjects - List of available subject identifiers
   * @returns {Array<Object>} Array of recommendation objects with subject and priority
   * 
   * @example
   * const recs = getLearningRecommendations(['math', 'phonics', 'reading']);
   * // Returns: [{ subject: 'math', priority: 'high', reason: '...' }, ...]
   */
  const getLearningRecommendations = useCallback((availableSubjects) => {
    if (!user || !isInitialized) return [];
    
    const recs = adaptiveLearningEngine.recommendLearningPath(user.id, availableSubjects);
    setRecommendations(recs);
    return recs;
  }, [user, isInitialized]);

  /**
   * Gets comprehensive performance analytics for a subject.
   * 
   * @param {string} subject - Subject identifier
   * @returns {Object} Analytics object with performance metrics
   * @returns {number} returns.totalAttempts - Total questions attempted
   * @returns {number} returns.accuracy - Accuracy percentage (0-100)
   * @returns {number} returns.averageResponseTime - Average time to answer in ms
   * @returns {number} returns.currentStreak - Current correct answer streak
   * @returns {string} returns.masteryLevel - Mastery level ('beginner', 'developing', 'proficient', 'expert')
   * @returns {number} returns.masteryScore - Mastery score (0-100)
   * @returns {string} returns.recentTrend - Trend indicator ('improving', 'declining', 'stable')
   * @returns {Array} returns.strongAreas - Array of strong topic areas
   * @returns {Array} returns.improvementAreas - Array of areas needing improvement
   * @returns {number} returns.consistency - Consistency score (0-100)
   * 
   * @example
   * const analytics = getSubjectAnalytics('math');
   * console.log(`Accuracy: ${analytics.accuracy}%`);
   * console.log(`Mastery: ${analytics.masteryLevel}`);
   */
  const getSubjectAnalytics = useCallback((subject) => {
    if (!user || !isInitialized) {
      return {
        totalAttempts: 0,
        accuracy: 0,
        averageResponseTime: 0,
        currentStreak: 0,
        masteryLevel: 'beginner',
        masteryScore: 0,
        recentTrend: 'stable',
        strongAreas: [],
        improvementAreas: [],
        consistency: 0
      };
    }
    
    return adaptiveLearningEngine.getPerformanceAnalytics(user.id, subject);
  }, [user, isInitialized]);

  /**
   * Get subject mastery level
   */
  const getSubjectMastery = useCallback((subject) => {
    if (!user || !isInitialized) return 'beginner';
    
    const analytics = adaptiveLearningEngine.getPerformanceAnalytics(user.id, subject);
    return analytics.masteryLevel;
  }, [user, isInitialized]);

  /**
   * Calculate accuracy for a subject
   */
  const getSubjectAccuracy = useCallback((subject, recentCount = 10) => {
    if (!user || !isInitialized) return 0;
    
    return Math.round(adaptiveLearningEngine.calculateAccuracy(user.id, subject, recentCount) * 100);
  }, [user, isInitialized]);

  /**
   * Get current streak for a subject
   */
  const getSubjectStreak = useCallback((subject) => {
    if (!user || !isInitialized) return 0;
    
    return adaptiveLearningEngine.calculateStreak(user.id, subject);
  }, [user, isInitialized]);

  /**
   * Start a new learning session
   */
  const startSession = useCallback((subject) => {
    sessionStartTime.current = Date.now();
    questionStartTime.current = Date.now();
    
    // Update performance data for the subject
    if (user && isInitialized) {
      setPerformanceData(prev => ({
        ...prev,
        [subject]: adaptiveLearningEngine.getPerformanceAnalytics(user.id, subject)
      }));
    }
  }, [user, isInitialized]);

  /**
   * End a learning session and track session-level metrics
   */
  const endSession = useCallback((subject, sessionResults) => {
    if (!user || !isInitialized) return;

    const sessionDuration = Date.now() - sessionStartTime.current;
    
    // Track session-level performance
    const sessionData = {
      subject,
      duration: sessionDuration,
      questionsAttempted: sessionResults.questionsAttempted || 0,
      correctAnswers: sessionResults.correctAnswers || 0,
      totalScore: sessionResults.totalScore || 0,
      averageResponseTime: sessionResults.averageResponseTime || 0,
      hintsUsed: sessionResults.hintsUsed || 0,
      timestamp: Date.now()
    };

    // Update performance data
    setPerformanceData(prev => ({
      ...prev,
      [subject]: adaptiveLearningEngine.getPerformanceAnalytics(user.id, subject)
    }));

    // Save data
    saveAdaptiveLearningData();

    return sessionData;
  }, [user, isInitialized]);

  /**
   * Get difficulty adjustment suggestion with explanation
   */
  const getDifficultyAdjustment = useCallback((subject, currentDifficulty) => {
    if (!user || !isInitialized) {
      return {
        suggestedDifficulty: currentDifficulty,
        shouldAdjust: false,
        reason: 'Insufficient data for adjustment'
      };
    }

    const suggestedDifficulty = adaptiveLearningEngine.adjustDifficulty(user.id, subject, currentDifficulty);
    const shouldAdjust = suggestedDifficulty !== currentDifficulty;
    
    let reason = 'No adjustment needed';
    if (shouldAdjust) {
      const analytics = adaptiveLearningEngine.getPerformanceAnalytics(user.id, subject);
      
      if (suggestedDifficulty === DIFFICULTY_LEVELS.HARD) {
        reason = `Great job! Your ${analytics.accuracy}% accuracy shows you're ready for harder questions.`;
      } else if (suggestedDifficulty === DIFFICULTY_LEVELS.EASY) {
        reason = `Let's practice more at an easier level to build confidence.`;
      } else {
        reason = `Adjusting to medium difficulty based on your progress.`;
      }
    }

    return {
      suggestedDifficulty,
      shouldAdjust,
      reason
    };
  }, [user, isInitialized]);

  /**
   * Get progress indicators for UI display
   */
  const getProgressIndicators = useCallback((subject) => {
    if (!user || !isInitialized) {
      return {
        masteryProgress: 0,
        accuracyTrend: 'stable',
        streakStatus: 'none',
        nextMilestone: 'Complete first question',
        strengthAreas: [],
        improvementAreas: []
      };
    }

    const analytics = adaptiveLearningEngine.getPerformanceAnalytics(user.id, subject);
    
    // Calculate mastery progress as percentage
    const masteryProgress = analytics.masteryScore;
    
    // Determine streak status
    let streakStatus = 'none';
    if (analytics.currentStreak >= 10) streakStatus = 'fire';
    else if (analytics.currentStreak >= 5) streakStatus = 'hot';
    else if (analytics.currentStreak >= 3) streakStatus = 'warm';
    
    // Determine next milestone
    let nextMilestone = 'Complete first question';
    if (analytics.totalAttempts === 0) {
      nextMilestone = 'Complete first question';
    } else if (analytics.accuracy < 70) {
      nextMilestone = 'Reach 70% accuracy';
    } else if (analytics.currentStreak < 5) {
      nextMilestone = 'Get 5 questions right in a row';
    } else if (analytics.masteryLevel === 'beginner') {
      nextMilestone = 'Reach developing level';
    } else if (analytics.masteryLevel === 'developing') {
      nextMilestone = 'Reach proficient level';
    } else if (analytics.masteryLevel === 'proficient') {
      nextMilestone = 'Become an expert';
    } else {
      nextMilestone = 'Maintain expert level';
    }

    return {
      masteryProgress,
      accuracyTrend: analytics.recentTrend,
      streakStatus,
      nextMilestone,
      strengthAreas: analytics.strongAreas,
      improvementAreas: analytics.improvementAreas
    };
  }, [user, isInitialized]);

  /**
   * Get personalized encouragement message
   */
  const getEncouragementMessage = useCallback((subject, result) => {
    if (!user || !isInitialized) {
      return result.isCorrect ? 'Great job!' : 'Keep trying!';
    }

    const analytics = adaptiveLearningEngine.getPerformanceAnalytics(user.id, subject);
    const streak = analytics.currentStreak;
    const accuracy = analytics.accuracy;

    if (result.isCorrect) {
      if (streak >= 10) return "🔥 You're on fire! Amazing streak!";
      if (streak >= 5) return "⭐ Fantastic! You're really getting the hang of this!";
      if (streak >= 3) return "🌟 Great job! You're building momentum!";
      if (accuracy >= 90) return "💯 Excellent work! You're mastering this!";
      if (accuracy >= 75) return "👏 Well done! You're doing great!";
      return "✅ Correct! Keep up the good work!";
    } else {
      if (streak === 0 && analytics.totalAttempts > 5) return "💪 Don't give up! Every expert was once a beginner!";
      if (accuracy < 50) return "🌱 You're learning! Each mistake helps you grow!";
      if (analytics.recentTrend === 'improving') return "📈 You're getting better! Keep practicing!";
      return "🤔 Not quite, but you're learning! Try again!";
    }
  }, [user, isInitialized]);

  return {
    // Core functionality
    trackQuestionAttempt,
    startQuestion,
    startSession,
    endSession,
    
    // Difficulty management
    getCurrentDifficulty,
    getDifficultyAdjustment,
    
    // Question selection
    selectQuestions,
    
    // Analytics and insights
    getSubjectAnalytics,
    getSubjectMastery,
    getSubjectAccuracy,
    getSubjectStreak,
    getLearningRecommendations,
    getProgressIndicators,
    getEncouragementMessage,
    
    // State
    isInitialized,
    performanceData,
    recommendations
  };
};

export default useAdaptiveLearning;